// src/components/store/sections/navbar/NavbarModernPill.tsx
"use client";

import { useState, useEffect, useRef } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import Image from "next/image";
import { 
  Search, 
  Heart, 
  Store, 
  Sparkles, 
  ChevronDown, 
  Check, 
  Loader2, 
  ArrowLeft, 
  X, 
  Menu, 
  Package, 
  Flame, 
  Layers, 
  ArrowRight,
  ShoppingBag,
  Camera,
} from "lucide-react";
import UserHeaderMenu from "@/components/store/UserHeaderMenu";
import CartHeaderButton from "@/components/store/CartHeaderButton";
import ShopCardsModal, { ShopItem } from "@/components/store/sections/shop/ShopCardsModal";
import VisualSearchModal from "./VisualSearchModal";
import { useNavbarBranding, NavbarBrandIdentity, NavbarBrandingConfig } from "./brandingConfig";

interface NavbarProps {
  subdomain?: string;
  storeName?: string;
  logo?: string | null;
  primaryColor?: string;
  secondaryColor?: string;
  storeTagline?: string;
  brandingConfig?: Partial<NavbarBrandingConfig>;
  isPreview?: boolean;
}

export default function NavbarModernPill({
  subdomain = "nextlook",
  storeName = "Nextlook",
  logo = "/logo.png",
  primaryColor = "#059669",
  secondaryColor = "#047857",
  storeTagline,
  brandingConfig,
}: NavbarProps) {
  const {
    branding,
    effectiveStoreName,
    effectiveTagline,
    effectiveLogo,
    effectivePrimaryColor,
    effectiveSecondaryColor,
    mainBarStyle,
  } = useNavbarBranding({
    subdomain,
    storeName,
    storeTagline,
    logo,
    primaryColor,
    secondaryColor,
    brandingConfig,
  });

  storeName = effectiveStoreName || storeName;
  logo = effectiveLogo;
  primaryColor = effectivePrimaryColor;
  secondaryColor = effectiveSecondaryColor;
  const router = useRouter();

  // Shop / Outlet States
  const [shopsList, setShopsList] = useState<ShopItem[]>([]);
  const [shopsLoading, setShopsLoading] = useState(true);
  const [selectedShopId, setSelectedShopId] = useState<string>("all");
  const [isShopMenuOpen, setIsShopMenuOpen] = useState(false);
  const [isAllShopsModalOpen, setIsAllShopsModalOpen] = useState(false);
  const shopDropdownRef = useRef<HTMLDivElement>(null);

  // Search States
  const [searchQuery, setSearchQuery] = useState("");
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
  const [isVisualSearchOpen, setIsVisualSearchOpen] = useState(false);
  const mobileSearchInputRef = useRef<HTMLInputElement>(null);

  // Mobile Drawer State
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      const shopParam = params.get("shop");
      if (shopParam) {
        setSelectedShopId(shopParam);
      } else {
        setSelectedShopId("all");
      }
    }
  }, []);

  useEffect(() => {
    if (isMobileSearchOpen && mobileSearchInputRef.current) {
      mobileSearchInputRef.current.focus();
    }
  }, [isMobileSearchOpen]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (shopDropdownRef.current && !shopDropdownRef.current.contains(e.target as Node)) {
        setIsShopMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Shops Fetch
  useEffect(() => {
    async function fetchShops() {
      setShopsLoading(true);
      try {
        const isLocal =
          typeof window !== "undefined" &&
          (window.location.hostname.includes("localhost") ||
            window.location.hostname === "127.0.0.1");

        const apiBase = isLocal ? `http://${subdomain}.localhost:8000` : "";
        const apiUrl = `${apiBase}/api/v1/stores/shops/`;

        const token =
          typeof window !== "undefined"
            ? localStorage.getItem("merchant_token") ||
              localStorage.getItem("token") ||
              localStorage.getItem("customer_token") ||
              ""
            : "";

        const res = await fetch(apiUrl, {
          headers: {
            Accept: "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });

        if (res.ok) {
          const data = await res.json();
          const list = Array.isArray(data)
            ? data
            : data?.results || data?.data || data?.shops || [];
          setShopsList(list);
        }
      } catch (err) {
        console.error("Error fetching shops in NavbarModernPill:", err);
      } finally {
        setShopsLoading(false);
      }
    }
    fetchShops();
  }, [subdomain]);

  const handleSelectShop = (shopId: string | number) => {
    setIsShopMenuOpen(false);
    setIsAllShopsModalOpen(false);
    setSelectedShopId(String(shopId));
    if (shopId === "all") {
      router.push("/products");
    } else {
      router.push(`/products?shop=${shopId}`);
    }
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsMobileSearchOpen(false);
    if (searchQuery.trim()) {
      const shopParam = selectedShopId !== "all" ? `&shop=${selectedShopId}` : "";
      router.push(`/products?search=${encodeURIComponent(searchQuery.trim())}${shopParam}`);
    }
  };

  const currentSelectedShop = shopsList.find((s) => String(s.id) === selectedShopId);
  const currentShopLabel = currentSelectedShop ? currentSelectedShop.name : "All Outlets";

  return (
    <header className="sticky top-0 z-50 w-full pt-2.5 sm:pt-3.5 px-2.5 sm:px-6 select-none font-sans">
      
      {/* ফ্লোটিং মডার্ন পিল কন্টেইনার */}
      <div style={mainBarStyle} className="max-w-7xl mx-auto rounded-full bg-white/95 backdrop-blur-md border border-slate-200/90 shadow-lg px-3 sm:px-6 py-2 sm:py-2.5 flex items-center justify-between gap-2 sm:gap-4 relative transition-all">
        
        {/* ১. মোবাইল ফুল-উইডথ সার্চ ওভারলে */}
        {isMobileSearchOpen && (
          <div className="absolute inset-0 z-50 bg-white rounded-full px-3.5 flex items-center gap-2 border border-slate-200 shadow-md animate-in fade-in duration-150">
            <button
              type="button"
              onClick={() => setIsMobileSearchOpen(false)}
              className="p-1 rounded-full hover:bg-slate-100 text-slate-500 transition-colors cursor-pointer shrink-0"
              aria-label="Back"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>

            <form onSubmit={handleSearchSubmit} className="flex-1 flex items-center h-full">
              <input
                ref={mobileSearchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search products, brands and more..."
                className="w-full text-xs text-slate-800 bg-transparent outline-none placeholder:text-slate-400"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery("")}
                  className="p-1 text-slate-400 hover:text-slate-600 cursor-pointer shrink-0"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  setIsMobileSearchOpen(false);
                  setIsVisualSearchOpen(true);
                }}
                title="Search by Photo or Camera"
                className="p-1.5 text-slate-500 hover:text-slate-800 cursor-pointer shrink-0"
              >
                <Camera className="w-4 h-4" style={{ color: primaryColor }} />
              </button>
              <button
                type="submit"
                style={{ color: primaryColor }}
                className="p-1.5 font-bold cursor-pointer shrink-0"
              >
                <Search className="w-4 h-4 stroke-[2.5]" />
              </button>
            </form>
          </div>
        )}

        {/* বাম পাশ */}
        <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">
          <button
            type="button"
            onClick={() => setMobileDrawerOpen(true)}
            className="lg:hidden p-1.5 rounded-full hover:bg-slate-100 text-slate-700 transition-colors cursor-pointer"
            aria-label="Menu"
          >
            <Menu className="w-5 h-5" />
          </button>

          <NavbarBrandIdentity
            href="/"
            branding={branding}
            storeName={storeName}
            tagline={effectiveTagline}
            logo={logo}
            primaryColor={primaryColor}
            secondaryColor={secondaryColor}
            isDarkTheme={false}
            variantStyle="modern_pill"
          />
        </div>

        {/* ২. Select Outlet Dropdown */}
        <div ref={shopDropdownRef} className="relative hidden xl:flex items-center shrink-0">
          <button
            type="button"
            onClick={() => setIsShopMenuOpen(!isShopMenuOpen)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-200/90 hover:border-slate-300 bg-slate-50/70 text-slate-700 text-xs font-bold transition-all cursor-pointer shadow-2xs"
            title="Select Outlet"
          >
            <Store className="w-3.5 h-3.5" style={{ color: primaryColor }} />
            <span className="truncate max-w-[90px]">{currentShopLabel}</span>
            <ChevronDown className={`w-3 h-3 text-slate-400 transition-transform ${isShopMenuOpen ? "rotate-180" : ""}`} />
          </button>

          {isShopMenuOpen && (
            <div className="absolute top-full left-0 mt-2.5 w-60 bg-white rounded-2xl shadow-xl border border-slate-100 z-[999] py-1.5 text-slate-800 animate-in fade-in zoom-in-95 duration-150">
              <div className="px-3.5 py-1.5 text-[10px] font-black uppercase tracking-wider text-slate-400">
                Select Outlet
              </div>
              <button
                type="button"
                onClick={() => handleSelectShop("all")}
                className={`w-full text-left px-3.5 py-2 text-xs font-bold hover:bg-slate-50 flex items-center justify-between cursor-pointer ${
                  selectedShopId === "all" ? "bg-slate-100 font-black" : ""
                }`}
                style={selectedShopId === "all" ? { color: primaryColor } : undefined}
              >
                <span>🏪 Main Store (All)</span>
                {selectedShopId === "all" && <Check className="w-3.5 h-3.5" />}
              </button>

              {shopsLoading ? (
                <div className="px-3.5 py-2.5 text-xs text-slate-400 flex items-center gap-2">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  <span>Loading outlets...</span>
                </div>
              ) : shopsList.length === 0 ? (
                <div className="px-3.5 py-2 text-xs text-slate-400 font-medium">
                  No other outlets found
                </div>
              ) : (
                shopsList.map((shop) => (
                  <button
                    key={shop.id}
                    type="button"
                    onClick={() => handleSelectShop(shop.id)}
                    className={`w-full text-left px-3.5 py-2 text-xs hover:bg-slate-50 flex items-center justify-between cursor-pointer truncate ${
                      String(shop.id) === selectedShopId ? "bg-slate-100 font-bold" : "text-slate-700"
                    }`}
                    style={String(shop.id) === selectedShopId ? { color: primaryColor } : undefined}
                  >
                    <div className="truncate">
                      <span className="truncate block font-bold">{shop.name}</span>
                      {shop.category_label && (
                        <span className="text-[9px] text-slate-400 font-mono block">
                          {shop.category_label}
                        </span>
                      )}
                    </div>
                    {String(shop.id) === selectedShopId && <Check className="w-3.5 h-3.5 shrink-0" />}
                  </button>
                ))
              )}
            </div>
          )}
        </div>

        {/* ৩. "Explore Shops" স্টোরি বাটন */}
        <div className="hidden lg:flex items-center shrink-0">
          <button
            type="button"
            onClick={() => setIsAllShopsModalOpen(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-purple-200 bg-purple-50/70 hover:bg-purple-100/80 text-purple-900 transition-all cursor-pointer group shadow-2xs"
            title="Explore All Shops & Stories"
          >
            <Sparkles className="w-3.5 h-3.5 text-purple-600 animate-pulse" />
            <span className="text-xs font-black">Shops</span>
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
          </button>
        </div>

        {/* ৪. সেন্ট্রাল লাইভ সার্চ বার */}
        <div className="hidden md:flex flex-1 max-w-sm lg:max-w-md items-center">
          <form 
            onSubmit={handleSearchSubmit}
            className="w-full flex items-center border border-slate-200 rounded-full pl-3.5 pr-1 py-1 bg-slate-50/60 focus-within:bg-white focus-within:border-slate-300 transition-all shadow-2xs"
          >
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search products..."
              className="flex-1 text-xs text-slate-800 bg-transparent outline-none placeholder:text-slate-400 font-medium"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery("")}
                className="p-1 mr-1 text-slate-400 hover:text-slate-600 cursor-pointer"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}

            {/* 🌟 Desktop Visual Search Camera / Lens Button */}
            <button
              type="button"
              onClick={() => setIsVisualSearchOpen(true)}
              title="Search by Photo or Camera"
              className="p-1.5 mr-1 rounded-full hover:bg-slate-200/70 text-slate-500 transition-colors cursor-pointer shrink-0"
            >
              <Camera className="w-3.5 h-3.5" style={{ color: primaryColor }} />
            </button>

            <button
              type="submit"
              style={{ backgroundColor: primaryColor }}
              className="p-1.5 rounded-full text-white hover:opacity-90 active:scale-95 transition-all flex items-center justify-center cursor-pointer shrink-0 shadow-xs"
              aria-label="Search"
            >
              <Search className="w-3.5 h-3.5 stroke-[2.5]" />
            </button>
          </form>
        </div>

        {/* ৫. ডান পাশ */}
        <div className="flex items-center gap-1 sm:gap-2.5 shrink-0">
          {/* Mobile Visual Search Camera Trigger */}
          <button
            type="button"
            onClick={() => setIsVisualSearchOpen(true)}
            className="hidden p-1.5 rounded-full hover:bg-slate-100 text-slate-700 transition-colors cursor-pointer"
            aria-label="Visual Search"
            title="Search by Photo or Camera"
          >
            <Camera className="w-4.5 h-4.5" style={{ color: primaryColor }} />
          </button>

          {/* Mobile Search Icon Trigger Button */}
          <button
            type="button"
            onClick={() => setIsMobileSearchOpen(true)}
            className="md:hidden p-1.5 rounded-full hover:bg-slate-100 text-slate-700 transition-colors cursor-pointer"
            aria-label="Search"
          >
            <Search className="w-4.5 h-4.5" />
          </button>

          {/* Mobile Quick Shops Pill */}
          <button
            type="button"
            onClick={() => setIsAllShopsModalOpen(true)}
            className="lg:hidden flex items-center gap-1 px-2.5 py-1 rounded-full border border-purple-200 bg-purple-50 text-purple-900 text-[11px] font-black shadow-2xs cursor-pointer"
          >
            <Sparkles className="w-3 h-3 text-purple-600" />
            <span>Shops</span>
          </button>

          <Link
            href="/wishlist"
            className="p-2 rounded-full hover:bg-slate-100 text-slate-600 transition-colors hidden sm:flex items-center justify-center"
            title="Wishlist"
          >
            <Heart className="w-4.5 h-4.5" />
          </Link>

          <UserHeaderMenu />
          <CartHeaderButton />
        </div>
      </div>

      {/* ৬. SHOP CARDS MODAL CALL */}
      <ShopCardsModal
        isOpen={isAllShopsModalOpen}
        onClose={() => setIsAllShopsModalOpen(false)}
        shopsList={shopsList}
        shopsLoading={shopsLoading}
        onSelectShop={handleSelectShop}
        primaryColor={primaryColor}
      />

      {/* 🌟 ৬.৫ AI VISUAL SEARCH MODAL */}
      <VisualSearchModal
        isOpen={isVisualSearchOpen}
        onClose={() => setIsVisualSearchOpen(false)}
        subdomain={subdomain}
        primaryColor={primaryColor}
      />

      {/* ৭. রেসপনসিভ মোবাইল ড্রয়ার */}
      {mobileDrawerOpen && (
        <div className="fixed inset-0 z-[999999] flex animate-in fade-in duration-200">
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-xs"
            onClick={() => setMobileDrawerOpen(false)}
          />

          <div className="relative w-72 sm:w-80 max-w-[85vw] bg-white text-slate-900 h-full shadow-2xl flex flex-col justify-between z-10 animate-in slide-in-from-left duration-300">
            <div>
              <div 
                className="text-white p-4 flex items-center justify-between"
                style={{ backgroundColor: primaryColor }}
              >
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center font-bold text-xs text-white">
                    {storeName.charAt(0)}
                  </div>
                  <span className="font-bold text-sm tracking-wide">Menu & Navigation</span>
                </div>
                <button
                  type="button"
                  onClick={() => setMobileDrawerOpen(false)}
                  className="p-1 rounded-md hover:bg-white/10 text-white cursor-pointer"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-4 space-y-4 overflow-y-auto max-h-[80vh]">
                {shopsList.length > 0 && (
                  <div>
                    <h4 className="text-xs font-black uppercase tracking-wider text-slate-400 mb-2">
                      Shops & Outlets
                    </h4>
                    <div className="space-y-1 text-xs font-bold text-slate-700">
                      <button
                        type="button"
                        onClick={() => {
                          handleSelectShop("all");
                          setMobileDrawerOpen(false);
                        }}
                        className={`w-full flex items-center justify-between p-2.5 rounded-xl text-left ${
                          selectedShopId === "all" ? "bg-slate-100 font-black" : "hover:bg-slate-50"
                        }`}
                        style={selectedShopId === "all" ? { color: primaryColor } : undefined}
                      >
                        <span>🏪 Main Store (All)</span>
                        {selectedShopId === "all" && <Check className="w-3.5 h-3.5" />}
                      </button>
                      {shopsList.map((shop) => (
                        <button
                          key={shop.id}
                          type="button"
                          onClick={() => {
                            handleSelectShop(shop.id);
                            setMobileDrawerOpen(false);
                          }}
                          className={`w-full flex items-center justify-between p-2.5 rounded-xl text-left ${
                            String(shop.id) === selectedShopId ? "bg-slate-100 font-black" : "hover:bg-slate-50"
                          }`}
                          style={String(shop.id) === selectedShopId ? { color: primaryColor } : undefined}
                        >
                          <div className="truncate">
                            <span className="block font-bold">{shop.name}</span>
                            {shop.category_label && (
                              <span className="text-[10px] text-slate-400 font-normal">{shop.category_label}</span>
                            )}
                          </div>
                          {String(shop.id) === selectedShopId && <Check className="w-3.5 h-3.5 shrink-0" />}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div className="border-t border-slate-100 pt-3">
                  <h4 className="text-xs font-black uppercase tracking-wider text-slate-400 mb-2">Explore</h4>
                  <div className="space-y-1 text-sm font-semibold text-slate-700">
                    <button
                      type="button"
                      onClick={() => {
                        setMobileDrawerOpen(false);
                        setIsAllShopsModalOpen(true);
                      }}
                      className="w-full flex items-center justify-between p-2 rounded-lg hover:bg-slate-100 text-left font-bold text-purple-700"
                    >
                      <div className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-purple-600" />
                        <span>All Brand Shops</span>
                      </div>
                      <ArrowRight className="w-4 h-4" />
                    </button>

                    <Link
                      href="/products?bestseller=true"
                      onClick={() => setMobileDrawerOpen(false)}
                      className="flex items-center justify-between p-2 rounded-lg hover:bg-slate-100"
                    >
                      <span>Best Sellers</span>
                      <Flame className="w-4 h-4 text-amber-500" />
                    </Link>

                    <Link
                      href="/categories"
                      onClick={() => setMobileDrawerOpen(false)}
                      className="flex items-center justify-between p-2 rounded-lg hover:bg-slate-100"
                    >
                      <span>All Categories</span>
                      <Layers className="w-4 h-4 text-slate-400" />
                    </Link>

                    <Link
                      href="/products?deals=true"
                      onClick={() => setMobileDrawerOpen(false)}
                      className="flex items-center justify-between p-2 rounded-lg hover:bg-slate-100"
                    >
                      <span>Today's Deals</span>
                      <Package className="w-4 h-4 text-slate-400" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-4 border-t border-slate-100 text-[10px] text-slate-400 font-mono text-center">
              © {storeName} • Modern Pill Navigation
            </div>
          </div>
        </div>
      )}
    </header>
  );
}